import React, { useEffect, useRef, useState } from "react";
import { useMotionValueEvent, useScroll, motion } from "framer-motion";
import { cn } from "@/lib/utils";

export const StickyScroll = ({
  content,
  contentClassName,
}: {
  content: {
    title: string;
    description: string;
    image?: string;
    content?: React.ReactNode | any;
  }[];
  contentClassName?: string;
}) => {
  const [activeCard, setActiveCard] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);
  const ref = useRef<any>(null);
  const textContainerRef = useRef<any>(null);
  const { scrollYProgress } = useScroll({
    // uncomment line 22 and comment line 23 if you DONT want the overflow container and want to have it change on the entire page scroll
    // target: ref
    container: ref,
    offset: ["start start", "end start"],
  });
  const cardLength = content.length;

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (!textContainerRef.current) return;

    // Get the sticky scroll container bounds
    const containerRect = ref.current?.getBoundingClientRect();
    if (!containerRect) return;

    // Get all text item containers
    const textItems = textContainerRef.current?.children;
    if (!textItems) return;

    // Find which text item is most centered in the viewport
    let bestMatch = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < textItems.length; i++) {
      const itemRect = textItems[i].getBoundingClientRect();

      // Calculate the center of the text item relative to the container
      const itemCenter = itemRect.top + itemRect.height / 2;
      const containerCenter = containerRect.top + containerRect.height / 2;

      // Distance from container center (closer to center = better match)
      const distance = Math.abs(itemCenter - containerCenter);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestMatch = i;
      }
    }

    // Ensure bestMatch is within valid bounds
    const validIndex = Math.max(0, Math.min(bestMatch, content.length - 1));
    setActiveCard(validIndex);
  });

  const backgroundColors = [
    "#F6F2EC", // light cream
    "#F6F2EC", // light cream
    "#F6F2EC", // light cream
  ];
  const linearGradients = [
    "linear-gradient(to bottom right, #A38648, #2B5D40)", // gold to green
    "linear-gradient(to bottom right, #5CA6D6, #2B5D40)", // blue to green
    "linear-gradient(to bottom right, #A38648, #5CA6D6)", // gold to blue
  ];

  const [backgroundGradient, setBackgroundGradient] = useState(
    linearGradients[0],
  );

  useEffect(() => {
    setBackgroundGradient(linearGradients[activeCard % linearGradients.length]);
  }, [activeCard]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <motion.div
      animate={{
        backgroundColor: backgroundColors[activeCard % backgroundColors.length],
      }}
      className="relative flex justify-center space-x-10 overflow-y-auto"
      style={{
        position: "relative",
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
        height: "600px",
        minHeight: "500px",
      }}
      ref={ref}
      data-oid="adiv90w"
    >
      <div
        className="div relative flex items-start px-8 z-10"
        data-oid="thga3ph"
      >
        <div className="max-w-4xl" ref={textContainerRef} data-oid=".gusc9h">
          {content.map((item, index) => (
            <motion.div
              key={item.title + index}
              className="my-8 lg:my-28 relative rounded-lg overflow-hidden lg:bg-transparent lg:rounded-none lg:overflow-visible lg:!bg-none"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: activeCard === index ? 1 : 0.1,
              }}
              style={{
                backgroundImage:
                  isMobile && item?.image ? `url(${item.image})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              data-oid="jsrc9ss"
            >
              {/* Mobile background overlay */}
              <div
                className="absolute inset-0 bg-black/50 lg:bg-transparent"
                data-oid="yznuxc0"
              />

              {/* Text content */}
              <div className="relative z-10 p-6 lg:p-0" data-oid="ccbks9h">
                <h2
                  className="text-3xl font-bold text-white lg:text-[#2B5D40]"
                  data-oid="gjpkshq"
                >
                  {item.title}
                </h2>
                <p
                  className="text-xl mt-12 max-w-md text-white lg:text-[#2B5D40] leading-relaxed"
                  data-oid=":s-97xi"
                >
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
          <div className="h-10 lg:h-40" data-oid="7qji7jf" />
        </div>
      </div>
      <div
        style={{ background: backgroundGradient }}
        className={cn(
          "sticky top-10 hidden h-[500px] w-[36rem] overflow-hidden bg-white rounded-lg lg:block",
          contentClassName,
        )}
        data-oid="es0pss7"
      >
        {content[activeCard]?.content ?? null}
      </div>
    </motion.div>
  );
};
